import { SCENARIO_DETAILS } from "@/types/rcp";
import { HAZARD_DESCRIPTIONS } from "@/utils/hazards";

const glossaryHeaders = ["Standard Term", "Description"];

const baseGlossaryRows = [
  [
    "Archetype",
    "An archetype is a representative asset class that allows the use of general or repeatable information and avoids having to uniquely specify all the characteristics of each individual asset analysed.",
  ],
  [
    "Climate Adjusted Value (CAV)",
    "Climate Adjusted Value. An adjusted value for the property, reduced to account for expected impacts of climate change, expressed in current day currency.",
  ],
  [
    "Failure Probability (FP)",
    "Expressed as a Decimal Fraction (multiply by 100 to get a percentage value). The failure probability is the annual probability of a climate hazard causing the asset to stop working with or without damage. This is reliant on the vulnerability of an archetype’s element to a particular hazard.",
  ],
  ["Coastal Inundation", HAZARD_DESCRIPTIONS.coastal_inundation],
  ["Extreme Heat", HAZARD_DESCRIPTIONS.heat],
  ["Extreme Wind", HAZARD_DESCRIPTIONS.wind],
  ["Forest Fire", HAZARD_DESCRIPTIONS.forest_fire],
  ["Freeze-Thaw", HAZARD_DESCRIPTIONS.freeze_thaw],
  ["Landslide", HAZARD_DESCRIPTIONS.landslide],
  ["Riverine Flooding", HAZARD_DESCRIPTIONS.flood_riverine],
  ["Soil Movement", HAZARD_DESCRIPTIONS.soil_movement],
  ["Surface Water Flooding", HAZARD_DESCRIPTIONS.flood_surfacewater],
  ["Tropical Cyclone Wind", HAZARD_DESCRIPTIONS.cyclone_wind],
  ["Tropical Cyclone Storm Surge", HAZARD_DESCRIPTIONS.cyclone_storm_surge],
  [
    "Maximum-to-date Value-At-Risk (MVAR)",
    "MVAR is an asset’s overall Maximum-to-date Value-At-Risk, and is a measure of an asset’s maximum potential damage, caused by climate-related hazards in any previous year.\nThe maximum-to-date adjustment ensures that no year has a lower risk than the previous year. It can be expressed as a decimal fraction (MVAR) or percentage (MVAR%) of an asset replacement cost. MVAR is based on the relative risk contribution of the structural elements that make up the asset in its engineering model. This means each element’s contribution (expressed as a percentage or fraction of the whole asset) and the annual probability of damage to each element are considered in the potential damage estimation.",
  ],
  [
    "Technical Insurance Premium (TIP)",
    "Technical Insurance Premium. The MVAR per asset for all hazard impacts (damage) combined. The TIP is based on the cost of damage to a property, using the replacement cost, expressed in current day dollars with no discounting or adjustments for other transaction costs.",
  ],
  [
    "High Risk Property % (HRP)",
    "The fraction of properties in the year specified that are ‘high risk’ i.e. 1% MVAR or higher. Expressed as a Decimal Fraction (multiply by 100 to get a percentage value).",
  ],
  [
    "Moderate Risk Property % (MRP)",
    "The fraction of properties in the year specified that are ‘moderate risk’ i.e. less than 0.2% MVAR to less than 1% MVAR. Expressed as a Decimal Fraction (multiply by 100 to get a percentage value).",
  ],
  [
    "Low Risk Property % (LRP)",
    "The fraction of properties in the year specified that are ‘low risk’ i.e. less than 0.2% MVAR. Expressed as a Decimal Fraction (multiply by 100 to get a percentage value).",
  ],
  [
    "High Risk Property Count (HRP#)",
    "The total count of properties in the year specified that are 'high risk' i.e. 1% MVAR or higher.",
  ],
  [
    "Moderate Risk Property Count (MRP#)",
    "The total count of properties in the year specified that are ‘moderate risk' i.e. from 0.2% MVAR to less than 1% MVAR.",
  ],
  [
    "Low Risk Property Count (LRP#)",
    "The total count of properties in the year specified that are ‘low risk' i.e. less than 0.2% MVAR.",
  ],
  [
    "Productivity Loss (PL)",
    "PL% reflects the effects of different types of disruption, including periods of closure associated with different hazard events. Productivity Loss is the percentage of total productive availability of the asset for which the asset is assumed to be unfit to operate due to component failure, damage or repair. Expressed as a Decimal Fraction (multiply by 100 to get a percentage value).",
  ],
  [
    "Total Technical Insurance Premium (TTIP)",
    "Total TIP (TTIP) is the sum of all TIPs for all properties in an area, such as all locations in a country. The TTIP is useful in drawing attention to the likely financial risk associated with climate change hazards.",
  ],
  [SCENARIO_DETAILS.rcp.title, SCENARIO_DETAILS.rcp.description],
  [SCENARIO_DETAILS.ssp.title, SCENARIO_DETAILS.ssp.description],
];

export const resilienceGlossaryRows = [
  [
    "Automated Resilience",
    "Automated Resilience is the automatic evaluation of every asset under a series of predefined resilience configurations. This provides a fast, standardised assessment of how physical climate risk outcomes may change under different design, construction, and resilience assumptions, enabling users to quickly identify where resilience investment is likely to have the greatest impact.",
  ],
  [
    "Baseline Pathway",
    "Represents the physical climate risk outcome calculated using the asset characteristics supplied by the user at the time of upload (or Hub defaults where user inputs are unavailable). In most instances, the Baseline pathway represents the best estimate of an asset's actual risk based on available information. It serves as the reference point against which alternative resilience pathways are compared.",
  ],
  [
    "Modern Resilience Pathway",
    "A standard resilience outcome for a modern asset built to common, present-day performance standards. Represents a plausible lower-bound risk outcome for the asset when asset characteristics are uncertain. Settings are automatically applied by the system",
  ],
  [
    "Low Resilience Pathway",
    "A lower resilience outcome for an older asset built to lower performance standards.  Represents a plausible upper-bound risk outcome for the asset when asset characteristics are uncertain. Settings are automatically applied by the system",
  ],
  [
    "Enhanced Resilience Pathway",
    "Enhanced Resilience outcome for a proactively adapted asset.  Represents a reasonably achievable adaptation scenario that extends beyond common, present-day building standards. Settings are automatically applied by the system",
  ],
];

/** The glossary table shipped with every output. */
export const getGlossaryRows = ({ showResilience }: { showResilience: boolean }): string[][] => [
  glossaryHeaders,
  ...baseGlossaryRows,
  ...(showResilience ? resilienceGlossaryRows : []),
];
